import { useLayoutEffect } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { ChatHubPage } from "@/components/pages/ChatHubPage";
import { AIMentorPage } from "@/components/pages/ai-mentor";
import { ResourcesPage } from "@/components/pages/ResourcesPage";
import { HistoryPage } from "@/components/pages/HistoryPage";
import { InsightsPage } from "@/components/pages/InsightsPage";
import NotFound from "./pages/NotFound";
import { ResourcesPDFPage } from "./components/pages/ResourcesPDFPage";
import { ResourcesAudioPage } from "./components/pages/ResourcesAudioPage";
import { ResourcesFavoritesPage } from "./components/pages/ResourcesFavoritesPage";
import { LoginPage } from "./components/pages/LoginPage";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AppContainer } from "./components/layout/AppContainer";
import { UserProfilePage } from "./components/pages/UserProfilePage";
import { FeedbackModal } from "./components/modals/FeedbackModal";
import { SurveyPage } from "./components/pages/survey";
import { SessionHistoryPage } from "./components/pages/SessoinHisroty";
import { RestorationRoomPage } from "@/components/pages/RestorationRoomPage";
import { ChatLegacyRedirect } from "@/components/routing/ChatLegacyRedirect";
import { PersonaNavGuard } from "@/components/routing/PersonaNavGuard";
import { MyTeamsPage, InviteTeammatesPage } from "@/components/pages/teams";
import { SystemAdminPage } from "@/components/pages/admin";
import { SystemAdminGuard } from "@/components/routing/SystemAdminGuard";
import { StrategicDecisionPage } from "@/components/pages/StrategicDecisionPage";
import { TeacherScenariosPage } from "@/components/pages/TeacherScenariosPage";
import { ProfilePage } from "@/components/pages/ProfilePage";
const surveyReleaseDate = new Date(2026, 11, 1); // December 1, 2026

const App = () => {
  const { isAuthenticated, userData, loading } = useAuth();

  const navigate = useNavigate();

  useLayoutEffect(() => {
    if (isAuthenticated && !userData && !loading) {
      navigate("/complete-profile");
    }
  }, [userData, navigate, isAuthenticated, loading]);

  useLayoutEffect(() => {
    if (new Date() < surveyReleaseDate) return;

    if (userData && !userData?.survey_completed_at && !userData?.survey) {
      navigate("/survey");
    }
  }, [userData, navigate]);

  return (
    <AppContainer>
      <Routes>
        <Route element={<ProtectedRoute />}>
          <Route element={<SystemAdminGuard />}>
            <Route path="/admin/system" element={<SystemAdminPage />} />
          </Route>
          <Route element={<PersonaNavGuard />}>
            <Route path="/" element={<ChatHubPage />} />
            <Route path="/chat" element={<ChatLegacyRedirect />} />
            <Route path="/find-coach" element={<Navigate to="/mentor" replace state={{ entry: "get_coaching" }} />} />
            <Route path="/coaching-waitlist" element={<Navigate to="/" replace />} />
            <Route path="/mentor" element={<AIMentorPage />} />
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/history/session/:sessionId" element={<SessionHistoryPage />} />
            <Route path="/resources/pdf" element={<ResourcesPDFPage />} />
            <Route path="/resources/audio" element={<ResourcesAudioPage />} />
            <Route path="/resources/favorites" element={<ResourcesFavoritesPage />} />
            <Route path="/restoration-room" element={<RestorationRoomPage />} />
            <Route
              path="/supports/renew-my-spark"
              element={<Navigate to="/mentor" replace state={{ entry: "renew_my_spark" }} />}
            />
            <Route path="/tools/think-through-decision" element={<StrategicDecisionPage />} />
            <Route path="/tools/teacher-scenarios" element={<TeacherScenariosPage />} />
            <Route
              path="/conversation-lab/board"
              element={<Navigate to="/mentor" replace state={{ entry: "navigate_board_relationships" }} />}
            />
            <Route path="/growth/insights" element={<InsightsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/admin/my-teams" element={<MyTeamsPage />} />
            <Route path="/admin/my-teams/:schoolId" element={<MyTeamsPage />} />
            <Route path="/admin/invite-teammates" element={<InviteTeammatesPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>

        <Route element={<ProtectedRoute withNavigation={false} />}>
          <Route path="/complete-profile" element={<UserProfilePage />} />
          <Route path="/survey" element={<SurveyPage />} />
        </Route>

        <Route path="/login" element={<LoginPage />} />
      </Routes>

      <FeedbackModal />
    </AppContainer>
  );
};

export default App;
